/**
 * Walkable geometry + nav grid built from a DungeonDef. This is a derived cache:
 * it is rebuilt from content and never stored in the World.
 */
import type { DungeonDef, Polygon, Rect, RoomId, Vec2 } from '../contracts';
import { closestOnPolygonEdge, dist, pointInPolygon, polygonBounds, rectContains } from './geometry';

/** Every unit collides with walls as a circle of at most this radius (big bosses still fit corridors). */
export const WALL_RADIUS = 0.5;
/** Clearance from walls required for nav-grid cells used for pathing. */
export const NAV_CLEARANCE = 1;
/** Upper bound on grid cells; the cell size grows past 1 yd for huge maps. */
const MAX_CELLS = 400_000;

interface Area {
  poly: Polygon;
  bounds: Rect;
  roomId: RoomId | null;
}

export interface NavGrid {
  x0: number;
  y0: number;
  cell: number;
  cols: number;
  rows: number;
  /** Cell centre is walkable. */
  base: Uint8Array;
  /** Cell centre has NAV_CLEARANCE to every wall (preferred for pathing). */
  clear: Uint8Array;
}

export interface NavData {
  /** No geometry: the whole plane is walkable. */
  open: boolean;
  walkable: Area[];
  obstacles: Area[];
  grid: NavGrid | null;
  /**
   * +zone: per grid cell, 0 = blocked everywhere, 1 = walkable everywhere, 2 = a boundary crosses it
   * (exact polygon test). Lets isWalkable skip the polygon loops away from edges (big open maps
   * with hundreds of obstacles). Null = always exact.
   */
  fast?: Uint8Array | null;
  /** +zone: obstacles bucketed on a coarse grid (exact tests only look at a point's bucket). */
  obsIndex?: ObstacleIndex | null;
}

/** +zone: obstacle ids per OBS_BUCKET-yd bucket. */
interface ObstacleIndex {
  x0: number;
  y0: number;
  cols: number;
  rows: number;
  lists: (number[] | undefined)[];
}
const OBS_BUCKET = 16;

function buildObsIndex(obstacles: readonly Area[]): ObstacleIndex | null {
  if (obstacles.length < 32) return null;
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (const o of obstacles) {
    x0 = Math.min(x0, o.bounds.x);
    y0 = Math.min(y0, o.bounds.y);
    x1 = Math.max(x1, o.bounds.x + o.bounds.w);
    y1 = Math.max(y1, o.bounds.y + o.bounds.h);
  }
  const cols = Math.max(1, Math.ceil((x1 - x0) / OBS_BUCKET) + 1);
  const rows = Math.max(1, Math.ceil((y1 - y0) / OBS_BUCKET) + 1);
  const idx: ObstacleIndex = { x0, y0, cols, rows, lists: new Array(cols * rows) };
  obstacles.forEach((o, i) => {
    const c0 = Math.floor((o.bounds.x - x0) / OBS_BUCKET);
    const r0 = Math.floor((o.bounds.y - y0) / OBS_BUCKET);
    const c1 = Math.floor((o.bounds.x + o.bounds.w - x0) / OBS_BUCKET);
    const r1 = Math.floor((o.bounds.y + o.bounds.h - y0) / OBS_BUCKET);
    for (let r = r0; r <= r1; r++) for (let c = c0; c <= c1; c++) (idx.lists[r * cols + c] ??= []).push(i);
  });
  return idx;
}

/** Obstacle ids whose bounds may contain p (null = check them all). */
function obstaclesAt(nav: NavData, p: Vec2): readonly number[] | null {
  const idx = nav.obsIndex;
  if (!idx) return null;
  const c = Math.floor((p.x - idx.x0) / OBS_BUCKET);
  const r = Math.floor((p.y - idx.y0) / OBS_BUCKET);
  if (c < 0 || r < 0 || c >= idx.cols || r >= idx.rows) return EMPTY_IDS;
  return idx.lists[r * idx.cols + c] ?? EMPTY_IDS;
}
const EMPTY_IDS: readonly number[] = [];

const OPEN_NAV: NavData = { open: true, walkable: [], obstacles: [], grid: null };

const area = (poly: Polygon, roomId: RoomId | null): Area => ({ poly, bounds: polygonBounds(poly), roomId });

/** Nav data depends only on the (immutable) DungeonDef, so it is shared across sims. */
const navByDungeon = new WeakMap<DungeonDef, NavData>();

export function buildNav(dungeon: DungeonDef | undefined): NavData {
  if (!dungeon || dungeon.rooms.length + dungeon.corridors.length === 0) return OPEN_NAV;
  let nav = navByDungeon.get(dungeon);
  if (!nav) {
    nav = buildNavUncached(dungeon);
    navByDungeon.set(dungeon, nav);
  }
  return nav;
}

function buildNavUncached(dungeon: DungeonDef): NavData {
  const nav: NavData = {
    open: false,
    walkable: [...dungeon.rooms.map((r) => area(r.shape, r.id)), ...dungeon.corridors.map((c) => area(c.shape, null))],
    obstacles: (dungeon.obstacles ?? []).map((o) => area(o, null)),
    grid: null,
  };
  nav.obsIndex = buildObsIndex(nav.obstacles);
  nav.grid = buildGrid(nav, dungeon.bounds);
  nav.fast = buildFast(nav, nav.grid);
  return nav;
}

/** Cells crossed by any walkable / obstacle edge (and their neighbours) are exact; the rest copy `base`. */
function buildFast(nav: NavData, g: NavGrid): Uint8Array {
  const fast = new Uint8Array(g.cols * g.rows);
  for (let i = 0; i < fast.length; i++) fast[i] = g.base[i]!;
  const mark = (x: number, y: number): void => {
    const c = Math.floor((x - g.x0) / g.cell);
    const r = Math.floor((y - g.y0) / g.cell);
    for (let dr = -1; dr <= 1; dr++)
      for (let dc = -1; dc <= 1; dc++) {
        const cc = c + dc;
        const rr = r + dr;
        if (cc >= 0 && rr >= 0 && cc < g.cols && rr < g.rows) fast[rr * g.cols + cc] = 2;
      }
  };
  for (const a of [...nav.walkable, ...nav.obstacles]) {
    const pts = a.poly.points;
    for (let i = 0; i < pts.length; i++) {
      const p = pts[i]!;
      const q = pts[(i + 1) % pts.length]!;
      const n = Math.max(1, Math.ceil(dist(p, q) / (g.cell * 0.5)));
      for (let k = 0; k <= n; k++) mark(p.x + ((q.x - p.x) * k) / n, p.y + ((q.y - p.y) * k) / n);
    }
  }
  return fast;
}

function buildGrid(nav: NavData, bounds: Rect): NavGrid {
  let minX = bounds.x;
  let minY = bounds.y;
  let maxX = bounds.x + bounds.w;
  let maxY = bounds.y + bounds.h;
  for (const a of nav.walkable) {
    minX = Math.min(minX, a.bounds.x);
    minY = Math.min(minY, a.bounds.y);
    maxX = Math.max(maxX, a.bounds.x + a.bounds.w);
    maxY = Math.max(maxY, a.bounds.y + a.bounds.h);
  }
  const w = maxX - minX;
  const h = maxY - minY;
  const cell = Math.max(1, Math.sqrt((w * h) / MAX_CELLS));
  const cols = Math.ceil(w / cell) + 2;
  const rows = Math.ceil(h / cell) + 2;
  const g: NavGrid = { x0: minX - cell, y0: minY - cell, cell, cols, rows, base: new Uint8Array(cols * rows), clear: new Uint8Array(cols * rows) };
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) if (isWalkable(nav, cellCenter(g, c, r))) g.base[r * cols + c] = 1;
  }
  // Cells deep inside walkable space are clear; only those near a boundary need the exact test.
  const k = Math.ceil(NAV_CLEARANCE / cell) + 1;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (g.base[r * cols + c] !== 1) continue;
      let interior = r >= k && c >= k && r < rows - k && c < cols - k;
      for (let dr = -k; interior && dr <= k; dr++) for (let dc = -k; interior && dc <= k; dc++) interior = g.base[(r + dr) * cols + c + dc] === 1;
      if (interior || circleWalkable(nav, cellCenter(g, c, r), NAV_CLEARANCE)) g.clear[r * cols + c] = 1;
    }
  }
  return g;
}

// ---- point queries -----------------------------------------------------------

export function isWalkable(nav: NavData, p: Vec2): boolean {
  if (nav.open) return true;
  const g = nav.grid;
  if (nav.fast && g) {
    const c = Math.floor((p.x - g.x0) / g.cell);
    const r = Math.floor((p.y - g.y0) / g.cell);
    if (c < 0 || r < 0 || c >= g.cols || r >= g.rows) return false;
    const f = nav.fast[r * g.cols + c];
    if (f === 0) return false;
    if (f === 1) return true;
  }
  let inside = false;
  for (const a of nav.walkable) {
    if (rectContains(a.bounds, p) && pointInPolygon(p, a.poly)) {
      inside = true;
      break;
    }
  }
  if (!inside) return false;
  const ids = obstaclesAt(nav, p);
  if (ids) {
    for (const i of ids) {
      const o = nav.obstacles[i]!;
      if (rectContains(o.bounds, p) && pointInPolygon(p, o.poly)) return false;
    }
    return true;
  }
  for (const o of nav.obstacles) if (rectContains(o.bounds, p) && pointInPolygon(p, o.poly)) return false;
  return true;
}

const RING = Array.from({ length: 8 }, (_, i) => ({ x: Math.cos((i * Math.PI) / 4), y: Math.sin((i * Math.PI) / 4) }));

/** Centre plus 8 rim samples are walkable (circle-vs-wall approximation). */
export function circleWalkable(nav: NavData, p: Vec2, r: number): boolean {
  if (nav.open) return true;
  if (!isWalkable(nav, p)) return false;
  if (r <= 0) return true;
  for (const d of RING) if (!isWalkable(nav, { x: p.x + d.x * r, y: p.y + d.y * r })) return false;
  return true;
}

export function roomAt(nav: NavData, p: Vec2): RoomId | null {
  for (const a of nav.walkable) if (a.roomId && rectContains(a.bounds, p) && pointInPolygon(p, a.poly)) return a.roomId;
  return null;
}

/** Nearest walkable point: closest point on any boundary, nudged across it. */
export function clampToWalkable(nav: NavData, p: Vec2): Vec2 {
  if (isWalkable(nav, p)) return { x: p.x, y: p.y };
  let best: Vec2 | null = null;
  let bestD = Infinity;
  for (const a of [...nav.walkable, ...nav.obstacles]) {
    const q = closestOnPolygonEdge(p, a.poly);
    const d = dist(p, q);
    if (d >= bestD) continue;
    // Step a little further along p→q so we land on the walkable side of the edge.
    const k = d === 0 ? 0 : 0.05 / d;
    const cand = { x: q.x + (q.x - p.x) * k, y: q.y + (q.y - p.y) * k };
    if (isWalkable(nav, cand)) {
      best = cand;
      bestD = d;
    }
  }
  if (best) return best;
  const g = nav.grid;
  if (g) {
    const c = nearestCell(g, g.base, p, Math.max(g.cols, g.rows));
    if (c !== null) return cellCenter(g, c % g.cols, Math.floor(c / g.cols));
  }
  return { x: p.x, y: p.y };
}

// ---- grid helpers ------------------------------------------------------------

function cellCenter(g: NavGrid, c: number, r: number): Vec2 {
  return { x: g.x0 + (c + 0.5) * g.cell, y: g.y0 + (r + 0.5) * g.cell };
}

function cellOf(g: NavGrid, p: Vec2): { c: number; r: number } {
  return { c: Math.floor((p.x - g.x0) / g.cell), r: Math.floor((p.y - g.y0) / g.cell) };
}

function passable(g: NavGrid, layer: Uint8Array, c: number, r: number): boolean {
  return c >= 0 && r >= 0 && c < g.cols && r < g.rows && layer[r * g.cols + c] === 1;
}

/** Nearest passable cell index by expanding square rings (deterministic scan order). */
function nearestCell(g: NavGrid, layer: Uint8Array, p: Vec2, maxRing: number): number | null {
  const { c, r } = cellOf(g, p);
  if (passable(g, layer, c, r)) return r * g.cols + c;
  for (let k = 1; k <= maxRing; k++) {
    let best: number | null = null;
    let bestD = Infinity;
    for (let dr = -k; dr <= k; dr++) {
      for (let dc = -k; dc <= k; dc++) {
        if (Math.max(Math.abs(dr), Math.abs(dc)) !== k) continue;
        if (!passable(g, layer, c + dc, r + dr)) continue;
        const d = dist(p, cellCenter(g, c + dc, r + dr));
        if (d < bestD) {
          bestD = d;
          best = (r + dr) * g.cols + (c + dc);
        }
      }
    }
    if (best !== null) return best;
  }
  return null;
}

/** Grid line-of-walk: every sample along a→b lies in a passable cell (endpoints get 1 cell of slack). */
function lineClear(nav: NavData, g: NavGrid, layer: Uint8Array, a: Vec2, b: Vec2): boolean {
  const len = dist(a, b);
  const steps = Math.max(1, Math.ceil(len / (g.cell * 0.25)));
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const p = { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
    const { c, r } = cellOf(g, p);
    if (passable(g, layer, c, r)) continue;
    const nearEnd = t * len <= g.cell || (1 - t) * len <= g.cell;
    if (nearEnd && isWalkable(nav, p)) continue;
    return false;
  }
  return true;
}

/** +patrols: line of sight = an unbroken walkable line (walls, pillars and closed doors block it). */
export function lineOfSight(nav: NavData, a: Vec2, b: Vec2): boolean {
  if (nav.open || !nav.grid) return true;
  return lineClear(nav, nav.grid, nav.grid.base, a, b);
}

// ---- A* ------------------------------------------------------------------------
// The grid has a one-cell border that is never passable, so neighbours of a passable
// cell are always in bounds and can be addressed by flat index offsets.

const SQRT2 = Math.SQRT2;
/** Weighted A*: slightly longer raw paths (smoothing straightens them) for far fewer expansions. */
const HEURISTIC_WEIGHT = 1.5;
const DC = [1, -1, 0, 0, 1, 1, -1, -1] as const;
const DR = [0, 0, 1, -1, 1, -1, 1, -1] as const;
const COST = [1, 1, 1, 1, SQRT2, SQRT2, SQRT2, SQRT2] as const;
/** Raw A* results cached per grid (pure function of layer + endpoints). */
const PATH_CACHE_SIZE = 512;

/** Reusable A* buffers per grid; `stamp` marks which entries belong to the current search. */
interface Scratch {
  gen: number;
  stamp: Uint32Array;
  closed: Uint32Array;
  g: Float64Array;
  f: Float64Array;
  h: Float64Array;
  came: Int32Array;
  heap: number[];
  cache: Map<string, number[] | null>;
}
const scratchByGrid = new WeakMap<NavGrid, Scratch>();

function scratchFor(grid: NavGrid): Scratch {
  let s = scratchByGrid.get(grid);
  if (!s) {
    const n = grid.cols * grid.rows;
    s = {
      gen: 0,
      stamp: new Uint32Array(n),
      closed: new Uint32Array(n),
      g: new Float64Array(n),
      f: new Float64Array(n),
      h: new Float64Array(n),
      came: new Int32Array(n),
      heap: [],
      cache: new Map(),
    };
    scratchByGrid.set(grid, s);
  }
  return s;
}

function astar(grid: NavGrid, layer: Uint8Array, start: number, goal: number): number[] | null {
  if (start === goal) return [start];
  const s = scratchFor(grid);
  const key = `${layer === grid.clear ? 'c' : 'b'}${start}:${goal}`;
  const hit = s.cache.get(key);
  if (hit !== undefined) return hit;
  const res = search(grid, layer, s, start, goal);
  if (s.cache.size >= PATH_CACHE_SIZE) s.cache.delete(s.cache.keys().next().value!);
  s.cache.set(key, res);
  return res;
}

function search(grid: NavGrid, layer: Uint8Array, s: Scratch, start: number, goal: number): number[] | null {
  s.gen += 1;
  const gen = s.gen;
  const cols = grid.cols;
  const gc = goal % cols;
  const gr = (goal - gc) / cols;
  const { stamp, closed, g, f, h, came } = s;
  const heap = s.heap;
  heap.length = 0;
  // Min-heap on f, tie-broken by h then index (deterministic).
  const less = (a: number, b: number): boolean => f[a]! < f[b]! || (f[a] === f[b] && (h[a]! < h[b]! || (h[a] === h[b] && a < b)));
  const push = (x: number): void => {
    let i = heap.length;
    heap.push(x);
    while (i > 0) {
      const p = (i - 1) >> 1;
      const px = heap[p]!;
      if (!less(x, px)) break;
      heap[i] = px;
      i = p;
    }
    heap[i] = x;
  };
  const pop = (): number => {
    const top = heap[0]!;
    const last = heap.pop()!;
    const n = heap.length;
    if (n > 0) {
      let i = 0;
      for (;;) {
        const l = 2 * i + 1;
        if (l >= n) break;
        const r = l + 1;
        const m = r < n && less(heap[r]!, heap[l]!) ? r : l;
        if (!less(heap[m]!, last)) break;
        heap[i] = heap[m]!;
        i = m;
      }
      heap[i] = last;
    }
    return top;
  };
  const touch = (i: number): void => {
    if (stamp[i] === gen) return;
    stamp[i] = gen;
    g[i] = Infinity;
    came[i] = -1;
    const c = i % cols;
    const dx = Math.abs(c - gc);
    const dy = Math.abs((i - c) / cols - gr);
    h[i] = dx > dy ? dx + (SQRT2 - 1) * dy : dy + (SQRT2 - 1) * dx;
  };

  touch(start);
  g[start] = 0;
  f[start] = HEURISTIC_WEIGHT * h[start]!;
  push(start);
  while (heap.length > 0) {
    const cur = pop();
    if (cur === goal) {
      const path = [cur];
      for (let k = came[cur]!; k >= 0; k = came[k]!) path.push(k);
      return path.reverse();
    }
    if (closed[cur] === gen) continue;
    closed[cur] = gen;
    const gcur = g[cur]!;
    for (let d = 0; d < 8; d++) {
      const dc = DC[d]!;
      const dr = DR[d]!;
      const ni = cur + dc + dr * cols;
      if (layer[ni] !== 1 || closed[ni] === gen) continue;
      // No corner cutting on diagonals.
      if (d >= 4 && (layer[cur + dc] !== 1 || layer[cur + dr * cols] !== 1)) continue;
      touch(ni);
      const ng = gcur + COST[d]!;
      if (ng < g[ni]!) {
        g[ni] = ng;
        came[ni] = cur;
        f[ni] = ng + HEURISTIC_WEIGHT * h[ni]!;
        push(ni);
      }
    }
  }
  return null;
}

/** Keep only cells where the step direction changes (smoothing candidates). */
function turningPoints(grid: NavGrid, cells: number[]): number[] {
  if (cells.length <= 2) return cells;
  const out = [cells[0]!];
  for (let i = 1; i < cells.length - 1; i++) {
    const a = cells[i - 1]!;
    const b = cells[i]!;
    const c = cells[i + 1]!;
    if (b - a !== c - b) out.push(b);
  }
  out.push(cells[cells.length - 1]!);
  return out;
}

/** Waypoints from `from` to `to` (excluding `from`), or null when unreachable. */
export function findPath(nav: NavData, from: Vec2, to: Vec2): Vec2[] | null {
  if (nav.open || !nav.grid) return [{ x: to.x, y: to.y }];
  const g = nav.grid;
  const target = isWalkable(nav, to) ? { x: to.x, y: to.y } : clampToWalkable(nav, to);
  for (const layer of [g.clear, g.base]) {
    if (lineClear(nav, g, layer, from, target)) return [target];
    const s = nearestCell(g, layer, from, 4);
    const e = nearestCell(g, layer, target, 4);
    if (s === null || e === null) continue;
    const cells = astar(g, layer, s, e);
    if (!cells) continue;
    const pts: Vec2[] = [from, ...turningPoints(g, cells).map((i) => cellCenter(g, i % g.cols, Math.floor(i / g.cols))), target];
    return smooth(nav, g, layer, pts);
  }
  return null;
}

/** Greedy string pulling: from each anchor, jump to the farthest directly reachable point. */
function smooth(nav: NavData, g: NavGrid, layer: Uint8Array, pts: Vec2[]): Vec2[] {
  const out: Vec2[] = [];
  const last = pts.length - 1;
  let i = 0;
  while (i < last) {
    let j = i + 1;
    while (j < last && lineClear(nav, g, layer, pts[i]!, pts[j + 1]!)) j++;
    out.push(pts[j]!);
    i = j;
  }
  return out;
}

// ---- dynamic blockers (doors) ---------------------------------------------------

/**
 * A copy of `nav` with extra blocking polygons (closed doors). The grid is patched
 * incrementally: only cells within the blockers' bounds (+ clearance) are recomputed.
 * The result is immutable and safe to share (it gets its own A* scratch + path cache).
 */
export function withBlockers(nav: NavData, blockers: readonly Polygon[]): NavData {
  if (nav.open || blockers.length === 0) return nav;
  const extra = blockers.map((b) => area(b, null));
  const out: NavData = { open: false, walkable: nav.walkable, obstacles: [...nav.obstacles, ...extra], grid: null, fast: null };
  out.obsIndex = buildObsIndex(out.obstacles);
  const g0 = nav.grid;
  if (!g0) return out;
  const g: NavGrid = { ...g0, base: g0.base.slice(), clear: g0.clear.slice() };
  out.grid = g;
  const pad = NAV_CLEARANCE + g.cell;
  if (nav.fast) {
    // The blockers' cells go back to the exact test.
    const fast = nav.fast.slice();
    for (const b of extra) {
      const c0 = Math.max(0, Math.floor((b.bounds.x - g.cell - g.x0) / g.cell));
      const r0 = Math.max(0, Math.floor((b.bounds.y - g.cell - g.y0) / g.cell));
      const c1 = Math.min(g.cols - 1, Math.ceil((b.bounds.x + b.bounds.w + g.cell - g.x0) / g.cell));
      const r1 = Math.min(g.rows - 1, Math.ceil((b.bounds.y + b.bounds.h + g.cell - g.y0) / g.cell));
      for (let r = r0; r <= r1; r++) for (let c = c0; c <= c1; c++) fast[r * g.cols + c] = 2;
    }
    out.fast = fast;
  }
  for (const b of extra) {
    const c0 = Math.max(0, Math.floor((b.bounds.x - pad - g.x0) / g.cell));
    const r0 = Math.max(0, Math.floor((b.bounds.y - pad - g.y0) / g.cell));
    const c1 = Math.min(g.cols - 1, Math.ceil((b.bounds.x + b.bounds.w + pad - g.x0) / g.cell));
    const r1 = Math.min(g.rows - 1, Math.ceil((b.bounds.y + b.bounds.h + pad - g.y0) / g.cell));
    for (let r = r0; r <= r1; r++) {
      for (let c = c0; c <= c1; c++) {
        const i = r * g.cols + c;
        if (g.base[i] !== 1) continue;
        const p = cellCenter(g, c, r);
        if (!isWalkable(out, p)) {
          g.base[i] = 0;
          g.clear[i] = 0;
        } else if (g.clear[i] === 1 && !circleWalkable(out, p, NAV_CLEARANCE)) g.clear[i] = 0;
      }
    }
  }
  return out;
}

// ---- +glide: free flight queries ------------------------------------------------

/** +glide: inside the map's walkable areas, obstacles ignored (the map bounds a flyer can never leave). */
export function insideArea(nav: NavData, p: Vec2): boolean {
  if (nav.open) return true;
  for (const a of nav.walkable) if (rectContains(a.bounds, p) && pointInPolygon(p, a.poly)) return true;
  return false;
}

/** +glide: indices (DungeonDef.obstacles order, then door blockers) of the obstacles containing p. */
export function obstacleIdsAt(nav: NavData, p: Vec2): number[] {
  const out: number[] = [];
  if (nav.open) return out;
  const ids = obstaclesAt(nav, p);
  if (ids) {
    for (const i of ids) {
      const o = nav.obstacles[i]!;
      if (rectContains(o.bounds, p) && pointInPolygon(p, o.poly)) out.push(i);
    }
    return out;
  }
  nav.obstacles.forEach((o, i) => {
    if (rectContains(o.bounds, p) && pointInPolygon(p, o.poly)) out.push(i);
  });
  return out;
}

/** +glide: per nav grid, the cells connected (4-neighbour, walkable centres) to the start: the ground a walker can reach. */
const reachByGrid = new WeakMap<NavGrid, Uint8Array>();
export function reachMask(nav: NavData, start: Vec2): Uint8Array | null {
  const g = nav.grid;
  if (!g) return null;
  let m = reachByGrid.get(g);
  if (m) return m;
  m = new Uint8Array(g.cols * g.rows);
  const s = nearestCell(g, g.clear, start, 64) ?? nearestCell(g, g.base, start, 64);
  if (s !== null) {
    const stack = [s];
    m[s] = 1;
    while (stack.length) {
      const i = stack.pop()!;
      const c = i % g.cols;
      const r = (i - c) / g.cols;
      if (c > 0 && g.base[i - 1] === 1 && !m[i - 1]) (m[i - 1] = 1), stack.push(i - 1);
      if (c < g.cols - 1 && g.base[i + 1] === 1 && !m[i + 1]) (m[i + 1] = 1), stack.push(i + 1);
      if (r > 0 && g.base[i - g.cols] === 1 && !m[i - g.cols]) (m[i - g.cols] = 1), stack.push(i - g.cols);
      if (r < g.rows - 1 && g.base[i + g.cols] === 1 && !m[i + g.cols]) (m[i + g.cols] = 1), stack.push(i + g.cols);
    }
  }
  reachByGrid.set(g, m);
  return m;
}

/** +glide: the cell under p is part of `mask` (the connected ground). */
export function inMask(nav: NavData, mask: Uint8Array, p: Vec2): boolean {
  const g = nav.grid;
  if (!g) return true;
  const { c, r } = cellOf(g, p);
  return c >= 0 && r >= 0 && c < g.cols && r < g.rows && mask[r * g.cols + c] === 1;
}

/**
 * +glide: where a flyer coming down at p may stand: p itself when it is walkable ground connected to
 * the start, else the nearest such cell's centre (clear cells preferred). Never a pocket a walker
 * could not leave.
 */
export function landingSpot(nav: NavData, mask: Uint8Array | null, p: Vec2): Vec2 {
  if (nav.open) return { x: p.x, y: p.y };
  const g = nav.grid;
  if (!g || !mask) return clampToWalkable(nav, p);
  if (isWalkable(nav, p) && inMask(nav, mask, p) && circleWalkable(nav, p, WALL_RADIUS)) return { x: p.x, y: p.y };
  const both = new Uint8Array(g.cols * g.rows);
  // Only the neighbourhood is scanned (a flyer is always near some ground); fall back to the whole mask.
  const { c, r } = cellOf(g, p);
  const R = Math.ceil(80 / g.cell);
  for (let rr = Math.max(0, r - R); rr <= Math.min(g.rows - 1, r + R); rr++)
    for (let cc = Math.max(0, c - R); cc <= Math.min(g.cols - 1, c + R); cc++) {
      const i = rr * g.cols + cc;
      if (mask[i] === 1 && g.clear[i] === 1) both[i] = 1;
    }
  const hit = nearestCell(g, both, p, R) ?? nearestCell(g, mask, p, Math.max(g.cols, g.rows));
  return hit === null ? clampToWalkable(nav, p) : cellCenter(g, hit % g.cols, Math.floor(hit / g.cols));
}
